import { z } from 'zod'

// List shapes mirror Refine's data provider (`getList`), so admin-web maps
// them 1:1: `pagination.currentPage` → `page`, `sorters` → `sort`,
// `filters` → `filters`, and `{ items, total }` → `{ data, total }`.

export const MAX_PAGE_SIZE = 100

export const sortOrderSchema = z.enum(['asc', 'desc'])

export const sortSchema = z.object({
  field: z.string().min(1),
  order: sortOrderSchema,
})

// Refine's `CrudOperators` without the logical `or` / `and` operators, which
// group nested filters instead of comparing a field
export const filterOperatorSchema = z.enum([
  'eq',
  'ne',
  'lt',
  'gt',
  'lte',
  'gte',
  'in',
  'nin',
  'ina',
  'nina',
  'contains',
  'ncontains',
  'containss',
  'ncontainss',
  'between',
  'nbetween',
  'null',
  'nnull',
  'startswith',
  'nstartswith',
  'startswiths',
  'nstartswiths',
  'endswith',
  'nendswith',
  'endswiths',
  'nendswiths',
])

export const filterSchema = z.object({
  field: z.string().min(1),
  operator: filterOperatorSchema,
  // depends on the operator (scalar, array for `in` / `between`, boolean for
  // `null`); the backend validates it per field
  value: z.unknown(),
})

export const listQuerySchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE),
  sort: z.array(sortSchema).optional(),
  filters: z.array(filterSchema).optional(),
})

export const listResultSchema = <Item extends z.ZodType>(itemSchema: Item) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number().int().min(0),
  })

export type SortOrder = z.infer<typeof sortOrderSchema>
export type Sort = z.infer<typeof sortSchema>
export type FilterOperator = z.infer<typeof filterOperatorSchema>
export type Filter = z.infer<typeof filterSchema>
export type ListQuery = z.infer<typeof listQuerySchema>
export type ListResult<Item> = { items: Item[]; total: number }
